// @flow
import { option, apply } from 'fp-ts';
import { type Option } from 'fp-ts/lib/Option';
import { type Either, fromNullable, left, either } from 'fp-ts/lib/Either';
import { traverse } from 'fp-ts/lib/Array';
import { compose } from 'fp-ts/lib/function';

import has from './has';

export const eProp = (name: string) => (obj: any) => {
  const leftMsg = `Unable to find ${name} in object.`;
  return has(obj)(name) ? fromNullable(leftMsg)(obj[name]) : left(leftMsg);
};

const ePropTuple = name => obj => eProp(name)(obj)
  .map(value => ({
    [name]: value,
  }));

// getObjValues :: [string] -> Object -> Either string Object
export const getObjValues = (xs: Array<string>) => (obj: Object): Either<string, Object> =>
  traverse(either)(compose(x => x(obj), ePropTuple), xs)
    .map(tupleObj => tupleObj.reduce((acc, item) => ({ ...acc, ...item }), {}));

export const optionFind = <T>(f: (x: T) => boolean, arr: Array<T>): Option<T> => option.fromNullable(arr.find(f));

export const equals = (a: any) => (b: any): boolean => b === a;

// Extracts an objects propery into a Option
export const prop = (name: string) => (obj: any) => (
  has(obj)(name) ? option.fromNullable(obj[name]) : option.none
);

export const logger = (label: string) => (x: *): * => {
  console.log(`${label}:`, x);
  return x;
};

// Curried operators
export const concat = (a1: Array<*>) => (a2: Array<*>) => (
  a1.concat(a2)
);

/* Takes a function - which takes any two values of the same type and returns one value of the same type - and returns a
   curried function that takes two Options and then applys the given function to both, returning an Option. */
export const combineOptions = (f: (x: *) => (y: *) => *) => (o1: Option<*>) => (o2: Option<*>) => (
  apply.liftA2(option)(f)(o1)(o2)
);

// Concatenates the contents of two Option<Array>s, yielding a None if either option is a None.
export const concat2 = combineOptions(concat);

// Concatenates two Option<Array>s, always returning an Array.
export const safeConcat = (a1: Option<Array<*>>) => (a2: Option<Array<*>>) => (
  combineOptions(
    concat,
  )(
    a1.alt(option.some([])),
  )(
    a2.alt(option.some([])),
  ).getOrElseValue([])
);

// Report error to New Relic if we can
export const nrError = (error) => {
  if (window.newrelic) {
    window.newrelic.noticeError(error);
  }
};

export const nrPageAction = pageAction => err => {
  if (process.env.NODE_ENV === 'production' && window.newrelic) {
    window.newrelic.addPageAction(pageAction, { err });
  }
};


// WEBPACK FOOTER //
// ./src/helpers/functions.js